import { IconFromSet, IconValue } from "@gettactic/components";
import React from "react";
import { Seg } from "../../../../ui/Seg";

export interface SpotStateIconPreviewProps {
  occupiedIcon?: IconValue;
  availableIcon?: IconValue;
  offsetX: number;
  offsetY: number;
  size: number;
  circleRadius: number;
  /** Custom icon color override — falls back to a neutral gray, same as
   *  the map canvas preview. */
  color?: string;
  /** Colored backdrop behind the icon, shown only while previewing the
   *  occupied icon (it's an occupied-only indicator everywhere else too). */
  occupiedBackgroundColor?: string;
}

const PREVIEW_SIZE = 120;

/** Live mini-preview of where the state icon lands relative to the dot,
 *  using the exact same offset math as the SVG viewer
 *  (InteractiveZoneSVG.tsx) — the X/Y/Size drag inputs have no visible
 *  effect anywhere else in the editor, so this is the only way to see what
 *  they do without switching to the live map. */
export const SpotStateIconPreview: React.FC<SpotStateIconPreviewProps> = ({
  occupiedIcon,
  availableIcon,
  offsetX,
  offsetY,
  size,
  circleRadius,
  color,
  occupiedBackgroundColor
}) => {
  const [previewState, setPreviewState] = React.useState<
    "occupied" | "available"
  >(occupiedIcon ? "occupied" : "available");

  const icon = previewState === "occupied" ? occupiedIcon : availableIcon;
  const center = PREVIEW_SIZE / 2;
  const background =
    previewState === "occupied" ? occupiedBackgroundColor : undefined;

  return (
    <div className="flex flex-col gap-2 rounded-md border border-neutral-200 bg-neutral-50 p-2">
      {occupiedIcon && availableIcon ? (
        <Seg<"occupied" | "available">
          label="Preview icon"
          value={previewState}
          onValueChange={setPreviewState}
          options={[
            { value: "occupied", ariaLabel: "Occupied", label: "Occupied" },
            { value: "available", ariaLabel: "Available", label: "Available" }
          ]}
        />
      ) : null}
      <svg
        width={PREVIEW_SIZE}
        height={PREVIEW_SIZE}
        viewBox={`0 0 ${PREVIEW_SIZE} ${PREVIEW_SIZE}`}
        className="self-center rounded bg-white"
      >
        {/* Dashed outline of the dot the icon replaces, for scale/position
            reference only — not rendered in the viewer. Always fixed at the
            dot's own center/radius, independent of the icon's offset — a
            separate circle below carries the backdrop fill so moving the
            icon doesn't drag this reference ring along with it. */}
        <circle
          cx={center}
          cy={center}
          r={circleRadius}
          fill="none"
          stroke="#cbd5e1"
          strokeDasharray="3 3"
        />
        {background && (
          <circle
            cx={center + offsetX}
            cy={center + offsetY}
            r={size / 2}
            fill={background}
          />
        )}
        {icon ? (
          <g
            transform={`translate(${center - size / 2 + offsetX}, ${center - size / 2 + offsetY})`}
          >
            <IconFromSet
              icon={icon}
              color={color || "#374151"}
              style={{ width: size, height: size }}
              preserveAspectRatio="xMidYMid meet"
            />
          </g>
        ) : (
          <text
            x={center}
            y={center}
            textAnchor="middle"
            dominantBaseline="middle"
            style={{ fontSize: 10, fill: "#9ca3af" }}
          >
            No icon selected
          </text>
        )}
      </svg>
    </div>
  );
};
